/**
 * Etiquetar um caso: o GANCHO que sabe fazê-lo, e o painel de um toque.
 *
 * As etiquetas tinham uma porta só — o gerenciador (`?etiquetas=<id>`), um modal
 * centrado de 560px com o catálogo do escritório, a barra de uso de cada
 * etiqueta e os itens de renomear e apagar. Ele existe por um bom motivo
 * (gerenciar o vocabulário do escritório num popover é a definição de esconder),
 * mas ele é a porta ERRADA para o gesto de todo dia: pôr "urgente" num caso é um
 * toque, e pagava por ele com a mesa inteira coberta por um véu, o caso que se
 * estava lendo escondido atrás da caixa e um "Pronto" para voltar.
 *
 * Agora são duas portas para duas coisas de tamanhos diferentes:
 *
 *   - **`EtiquetasRapidas`** (aqui): um `Popover` ancorado no botão, com as
 *     etiquetas do caso, a busca que também cria, e o catálogo em lista curta. A
 *     conversa continua na tela ao lado — é o que faz "marcar o que é urgente
 *     varrendo a mesa" ser um gesto e não uma viagem.
 *   - **o gerenciador** (`sobreposicoes/Etiquetas.jsx`): renomear, apagar e
 *     PINTAR, que valem para todos os casos e perguntam antes. O pé do popover
 *     leva até lá.
 *
 * O gancho fica aqui, e não lá, porque as duas superfícies têm de concordar
 * sobre o que "pôr uma etiqueta" faz: qual é a lista do caso quando ele não está
 * aberto, o que se relê depois de gravar, e por que tirar não pergunta. Duas
 * cópias dessa resposta é como uma delas envelhece — foi o caminho por onde a
 * etiqueta já ficou de fora do recorte da mesa uma vez.
 */
import { useMemo, useState } from "react";
import * as api from "../api.js";
import { useAcoes, useEstado, usePode } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { ICONES, Icone, tinta } from "../visual.jsx";
import { chaveDaEtiqueta, coresDoCatalogo, oQueEnterFaz } from "../etiquetas.js";
import { Botao } from "./comuns.jsx";
import { Input } from "@/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/popover";

/**
 * Tudo que se sabe sobre as etiquetas de UM caso, e as duas escritas.
 *
 * `alvo` é o id do caso — nunca "o aberto": etiquetar acontece também pelo menu
 * de contexto de uma LINHA da mesa, que quase nunca é a conversa aberta.
 */
export function useEtiquetasDoCaso(alvo) {
  const { caso, casoId: idDoCasoAberto, mesa, catalogoDeTags } = useEstado();
  const { avisar, carregarMesa, carregarCaso } = useAcoes();
  /* Etiquetar é editar o caso — a mesma permissão de mexer na ficha. Sem ela LER
     e FILTRAR continuam valendo: é trabalho de quem só atende, e fechar a porta
     esconderia o recorte de quem mais precisa dele. */
  const podeEditar = usePode()("registros:editar");
  const [ocupado, setOcupado] = useState("");

  const linha = mesa.find((c) => c.id === alvo);
  const nome = (alvo === idDoCasoAberto ? caso?.registro?.nome : linha?.nome) ?? linha?.nome ?? "este atendimento";

  /* As do caso vêm de dois lugares porque o caso pode não estar aberto: array na
     leitura do caso, texto com `|` na linha da mesa. */
  const minhas = useMemo(() => {
    if (alvo === idDoCasoAberto && Array.isArray(caso?.tags)) return caso.tags;
    return linha?.tags ? String(linha.tags).split("|").filter(Boolean) : [];
  }, [alvo, idDoCasoAberto, caso?.tags, linha?.tags]);

  const temSet = useMemo(() => new Set(minhas.map(chaveDaEtiqueta)), [minhas]);
  const catalogo = catalogoDeTags ?? [];
  const cores = useMemo(() => coresDoCatalogo(catalogoDeTags), [catalogoDeTags]);

  /*
   * Uma mexida, e o que ela obriga a reler.
   *
   * A mesa SEMPRE relê: etiqueta muda a linha do caso e o catálogo do filtro,
   * que é o que a coluna mostra. O caso relê só quando é o aberto — recarregar a
   * conversa de outra pessoa por causa de uma etiqueta posta de passagem é
   * pedido a mais e uma trilha que pisca sem motivo.
   */
  const mexer = async (marca, fn) => {
    setOcupado(marca);
    try {
      await fn();
      await carregarMesa();
      if (alvo === idDoCasoAberto) await carregarCaso(alvo);
    } catch (e) {
      avisar(e.message);
    } finally {
      setOcupado("");
    }
  };

  /* guarda-ok: pergunta — pôr e TIRAR do caso é o gesto mais repetido do dia e se
     desfaz no mesmo toque, na mesma linha. Perguntar aqui seria pedágio duas
     vezes por conversa. Renomear, apagar e pintar, que valem para todos os
     casos, perguntam no gerenciador. */
  const alternar = (tag) =>
    void mexer(
      chaveDaEtiqueta(tag),
      temSet.has(chaveDaEtiqueta(tag)) ? () => api.tirarEtiqueta(alvo, tag) : () => api.porEtiqueta(alvo, tag),
    );

  /*
   * O que o campo faz — Enter e botão pela MESMA porta.
   *
   * `oQueEnterFaz` (`etiquetas.js`, preso em `etiquetas.test.jsx`) responde se é
   * para criar, para marcar a que já existe com outra caixa/acento, ou para não
   * fazer nada. A CHAMADA é uma só: `porEtiqueta` cria se o rótulo não existir e
   * reaproveita se existir.
   */
  const escrever = (texto) => {
    const { acao, tag } = oQueEnterFaz(texto, catalogo, minhas);
    if (acao === "nada") return false;
    void mexer(chaveDaEtiqueta(tag), () => api.porEtiqueta(alvo, tag));
    return true;
  };

  return { nome, minhas, temSet, catalogo, cores, podeEditar, ocupado, mexer, alternar, escrever };
}

/**
 * Etiquetar num toque, ancorado no botão que abriu — sem cobrir a mesa.
 *
 * `render` no gatilho para o botão ser o do chamador (o "+" da ficha, com a
 * medida e a dica dele): embrulhar num `<div>` mudaria o layout da linha onde a
 * peça mora.
 */
export default function EtiquetasRapidas({ alvo, children, classe, rotulo = "Etiquetas deste atendimento" }) {
  const [aberto, setAberto] = useState(false);
  const [texto, setTexto] = useState("");
  const { ajustar } = useNavegacao();
  const { minhas, temSet, catalogo, podeEditar, ocupado, alternar, escrever } = useEtiquetasDoCaso(alvo);

  const busca = chaveDaEtiqueta(texto);
  const filtrado = busca ? catalogo.filter((c) => chaveDaEtiqueta(c.tag).includes(busca)) : catalogo;
  const jaExiste = busca ? catalogo.some((c) => chaveDaEtiqueta(c.tag) === busca) : false;

  return (
    <Popover
      open={aberto}
      onOpenChange={(v) => {
        setAberto(v);
        /* Fechar limpa o campo: reabrir com a busca da vez passada esconde o
           catálogo inteiro e parece que o escritório perdeu as etiquetas. */
        if (!v) setTexto("");
      }}
    >
      <PopoverTrigger render={<button type="button" aria-label={rotulo} data-dica={rotulo} className={classe} />}>
        {children}
      </PopoverTrigger>
      {/* `z-[120]`: painel de portal nasce acima da caixa que o abriu. */}
      <PopoverContent
        align="start"
        sideOffset={6}
        className="z-[120] w-[264px] rounded-xl border border-foreground/12 bg-card p-2.5 shadow-[0_14px_36px_color-mix(in_oklab,_var(--color-foreground)_16%,_transparent)]"
      >
        <Input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") return setTexto("");
            if (e.key !== "Enter") return;
            e.preventDefault();
            if (!podeEditar) return;
            if (escrever(texto)) setTexto("");
          }}
          placeholder={podeEditar ? "Procurar, ou escrever uma nova…" : "Procurar…"}
          aria-label={podeEditar ? "Procurar ou criar uma etiqueta" : "Procurar uma etiqueta"}
          className="mb-1.5 h-auto rounded-lg p-[7px_9px] text-corpo"
        />

        {podeEditar && texto.trim() && !jaExiste ? (
          <Botao
            destaque
            icone={ICONES.somar}
            rotulo={`Criar “${texto.trim()}”`}
            aoTocar={() => escrever(texto) && setTexto("")}
            desabilitado={ocupado === busca}
            estilo="width:100%;justify-content:center;margin-bottom:6px"
          />
        ) : null}

        {/* A lista rola dentro do painel: com quarenta etiquetas o popover
            passaria da altura da tela e o pé (o "Gerenciar") sairia dela. Não é
            `ScrollArea` — é uma lista curta com teto, e o `Rolagem` da casa é
            para pane de vista. */}
        <div className="max-h-[240px] overflow-y-auto">
          {filtrado.length ? (
            filtrado.map((c) => {
              const tem = temSet.has(chaveDaEtiqueta(c.tag));
              const [fundo, letra] = tinta(c.tag, c.cor);
              const Linha = podeEditar ? "button" : "div";
              return (
                <Linha
                  key={c.tag}
                  {...(podeEditar
                    ? {
                        type: "button",
                        onClick: () => alternar(c.tag),
                        disabled: ocupado === chaveDaEtiqueta(c.tag),
                        "aria-pressed": tem,
                      }
                    : {})}
                  className={`flex w-full items-center gap-2 rounded-lg border-none bg-transparent p-[5px_6px] text-left ${
                    podeEditar ? "lin cursor-pointer disabled:cursor-wait disabled:opacity-50" : ""
                  }`}
                >
                  {/* O visto ocupa o lugar mesmo vazio: sem isso a linha anda 2px
                      a cada toque, e lista que pula sob o dedo faz errar a
                      próxima. */}
                  <span
                    className={`flex size-[16px] flex-none items-center justify-center rounded-[5px] border ${
                      tem ? "border-transparent bg-verde text-creme" : "border-foreground/20"
                    }`}
                  >
                    {tem ? <Icone de={ICONES.certo} tamanho={11} largura={2.6} /> : null}
                  </span>
                  <span
                    className="min-w-0 flex-1 truncate rounded-sm px-[6px] py-[2px] text-selo font-semibold"
                    style={{ background: fundo, color: letra }}
                  >
                    {c.tag}
                  </span>
                  <span className="flex-none font-mono text-selo text-muted-foreground">{c.n ?? 0}</span>
                </Linha>
              );
            })
          ) : (
            /* Sem `Vazio` da casa: aqui não há espaço para título e botão, e a
               saída é o campo logo acima — que já está na tela, com o texto
               digitado. A frase diz qual é. */
            <p className="m-0 p-[10px_6px] text-apoio leading-[1.45] text-muted-foreground">
              {!podeEditar
                ? "Quem libera etiquetar é um sócio, em Equipe e acessos."
                : busca
                  ? "Nenhuma com esse nome — Enter cria esta."
                  : "O escritório ainda não tem etiquetas. A primeira nasce escrevendo aqui em cima."}
            </p>
          )}
        </div>

        {/* A porta para o que vale em TODOS os casos. Fecha o popover ao ir: o
            gerenciador é modal, e os dois abertos deixariam o painel por baixo
            do véu, visível e sem clique. */}
        <button
          type="button"
          onClick={() => {
            setAberto(false);
            ajustar({ etiquetas: alvo });
          }}
          className="lin mt-1 flex w-full cursor-pointer items-center gap-2 rounded-lg border-t border-t-border bg-transparent p-[8px_6px] text-left text-apoio text-tinta-suave"
        >
          <Icone de={ICONES.settings} tamanho={14} largura={1.9} />
          <span className="min-w-0 flex-1">Renomear, pintar ou apagar…</span>
          <span className="flex-none font-mono text-selo text-muted-foreground">{minhas.length}/8</span>
        </button>
      </PopoverContent>
    </Popover>
  );
}
